import { h, Component, Fragment } from "preact";
import type { ComponentChildren } from "preact";
import { api } from "../api";

/* ---------- Types ---------- */

interface ProviderSummary {
  id: string;
  type: string;
  displayName: string;
  enabled: boolean;
  connected?: boolean;
  status: "ok" | "error" | "not_connected" | "disabled";
  /** Explanation for an unhealthy status (bad credentials, API unreachable, etc.). */
  healthReason?: string | null;
  /** True when the error is specifically an authentication failure. */
  authError?: boolean;
}

interface ProviderDetail extends ProviderSummary {
  config: Record<string, unknown>;
  webhookUrls: string[];
  /** 46elks only: MediaBridge audio WebSocket URL to register in the 46elks dashboard. */
  audioWsUrl?: string;
}

interface FieldError {
  field: string;
  message: string;
}

interface ModemStatus {
  connected: boolean;
  signal: number | null;
  network: string | null;
  operator: string | null;
  band: string | null;
  networkTech: string | null;
  modemModel: string | null;
  modemManufacturer: string | null;
  firmware: string | null;
  imei: string | null;
  imsi: string | null;
  iccid: string | null;
  msisdn: string | null;
  stale: string[] | null;
  modemUnsupportedWarning: string | null;
}

interface Notification {
  message: string;
  type: "success" | "error";
  id: number;
}

/* ---------- Config field definitions per provider type ---------- */

interface ConfigFieldDef {
  name: string;
  label: string;
  type: "text" | "password" | "textarea" | "checkbox";
  required: boolean;
  /** For checkbox fields: value used when the provider has no stored value yet. */
  defaultChecked?: boolean;
  /** Optional hint rendered below the field. */
  hint?: string;
  /** For checkbox fields: hint shown only when the box is unchecked. */
  offHint?: string;
  /**
   * Name of another (checkbox) field this field depends on. When set, this
   * field is only rendered while that checkbox is checked. Used to reveal the
   * webhook IP override list only when the operator opts in.
   */
  showWhen?: string;
  /** Placeholder text for text/textarea inputs. */
  placeholder?: string;
}

const CONFIG_FIELDS: Record<string, ConfigFieldDef[]> = {
  vonage: [
    { name: "api_key", label: "API Key", type: "text", required: true },
    { name: "api_secret", label: "API Secret", type: "password", required: true },
    {
      name: "signature_secret",
      label: "Signature Secret",
      type: "password",
      required: false,
      hint:
        "Vonage account-level signature secret used to verify inbound webhook signatures. This is different from the API Secret and is found under Dashboard > Settings (not the Application). Leave empty to fall back to the API Secret.",
    },
    { name: "application_id", label: "Application ID", type: "text", required: true },
    { name: "private_key", label: "Private Key (PEM)", type: "textarea", required: true },
    {
      name: "webhook_validation",
      label: "Webhook validation",
      type: "checkbox",
      required: false,
      defaultChecked: true,
      offHint:
        "Incoming webhooks are not signature-verified. Anyone who knows the webhook URL can post events to this provider.",
    },
  ],
  "46elks": [
    { name: "api_username", label: "API Username", type: "text", required: true },
    { name: "api_password", label: "API Password", type: "password", required: true },
    { name: "websocket_number", label: "WebSocket Number", type: "text", required: false },
    {
      name: "webhook_validation",
      label: "Webhook validation",
      type: "checkbox",
      required: false,
      defaultChecked: true,
      hint: "Verifies that incoming webhooks originate from a 46elks IP address. 46elks does not sign webhooks, so this IP allowlist is the only available origin check.",
      offHint:
        "Incoming webhooks are not origin-verified. Anyone who knows the webhook URL can post events to this provider.",
    },
    {
      name: "override_webhook_whitelist",
      label: "Override webhook whitelist",
      type: "checkbox",
      required: false,
      defaultChecked: false,
      showWhen: "webhook_validation",
      hint: "Replace the built-in 46elks IP allowlist with your own. Only change this if 46elks has notified you of new callback IPs.",
    },
    {
      name: "webhook_ip_allowlist",
      label: "Allowed webhook IPs",
      type: "textarea",
      required: false,
      showWhen: "override_webhook_whitelist",
      placeholder: "176.10.154.199\n85.24.146.132\n185.39.146.243\n2001:9b0:2:902::199",
      hint: "One IP per line (or comma-separated). Leave empty to use the built-in 46elks defaults.",
    },
  ],
  dummy: [],
  "modem-gateway": [],
};

/**
 * Config field names that exist only to drive the form UI (e.g. reveal a
 * dependent field) and must never be sent to / persisted by the backend.
 */
const UI_ONLY_CONFIG_FIELDS: readonly string[] = ["override_webhook_whitelist"];

/**
 * Build the initial form config for a provider type, seeding checkbox fields
 * with their default value so toggles start in the correct state.
 */
function buildDefaultConfig(type: string): Record<string, string | boolean> {
  const config: Record<string, string | boolean> = {};
  for (const field of CONFIG_FIELDS[type] || []) {
    if (field.type === "checkbox") {
      config[field.name] = field.defaultChecked ?? false;
    }
  }
  return config;
}

/* ---------- Webhook endpoint labels ---------- */

/**
 * Friendly titles for webhook endpoints, keyed by the endpoint suffix (the last
 * path segment of a webhook URL). Covers all provider types.
 */
const WEBHOOK_ENDPOINT_LABELS: Record<string, string> = {
  // Vonage
  answer: "Answer (voice)",
  event: "Call events",
  "inbound-sms": "Inbound SMS",
  "sms-status": "SMS status",
  // 46elks
  voice_start: "Voice start",
  sms_incoming: "Inbound SMS",
};

/**
 * Derive a human-friendly title for a webhook URL from its endpoint suffix.
 * Falls back to the raw suffix if the endpoint is not in the label map.
 */
function webhookEndpointLabel(url: string): string {
  const suffix = url.split("?")[0].replace(/\/+$/, "").split("/").pop() ?? "";
  return WEBHOOK_ENDPOINT_LABELS[suffix] ?? suffix ?? "Webhook";
}

/* ---------- Provider config detail formatting ---------- */

/**
 * Friendly labels for provider config keys shown in the detail view. Covers
 * every provider type's config keys, including ones that aren't editable form
 * fields (e.g. pairing_secret, webhook_base_url).
 */
const CONFIG_KEY_LABELS: Record<string, string> = {
  api_key: "API Key",
  api_secret: "API Secret",
  signature_secret: "Signature Secret",
  application_id: "Application ID",
  private_key: "Private Key",
  private_key_path: "Private Key Path",
  webhook_base_url: "Webhook Base URL",
  webhook_validation: "Webhook validation",
  webhook_ip_allowlist: "Allowed webhook IPs",
  api_username: "API Username",
  api_password: "API Password",
  websocket_number: "WebSocket Number",
  pairing_secret: "Pairing Secret",
  name: "Name",
  supports_sips: "SIP support",
};

/**
 * Turn a raw config key into a friendly label. Falls back to Title Case of the
 * key (underscores to spaces) for any key not in the map.
 */
function configKeyLabel(key: string): string {
  if (CONFIG_KEY_LABELS[key]) return CONFIG_KEY_LABELS[key];
  return key
    .split("_")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/**
 * Format a raw config value for display. Booleans render as On/Off, empty
 * values as an em dash, everything else as its string form (secrets arrive
 * already masked from the server).
 */
function configValueDisplay(value: unknown): string {
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "—";
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

/**
 * Material-style outline trash icon. Rendered as an inline SVG so it inherits
 * the current text color (`currentColor`) and needs no extra assets.
 */
function trashIcon() {
  return (
    <svg
      class="icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

/**
 * Friendly display label for a provider type, used in the list overview.
 * The detail view intentionally shows the raw type (e.g. "modem-gateway")
 * since that screen is technical/config-oriented.
 */
const PROVIDER_TYPE_LABELS: Record<string, string> = {
  vonage: "Vonage",
  "46elks": "46elks",
  "modem-gateway": "Modem",
  dummy: "Dummy",
};

function providerTypeLabel(type: string): string {
  return PROVIDER_TYPE_LABELS[type] ?? type;
}

/**
 * Human-readable label for a provider's unified status. An error caused by an
 * authentication failure is called out specifically as "Authentication Error".
 */
function providerStatusLabel(p: ProviderSummary): string {
  switch (p.status) {
    case "ok":
      return "OK";
    case "not_connected":
      return "Not Connected";
    case "disabled":
      return "Disabled";
    default:
      return p.authError ? "Authentication Error" : "Error";
  }
}

/* ---------- Pairing secret generation (client-side) ---------- */

/**
 * Generate a pairing secret: 6-8 case-insensitive alphanumeric characters.
 * Uses crypto.getRandomValues() for secure generation in the browser.
 */
function generatePairingSecret(): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  const randomByte = new Uint8Array(1);
  crypto.getRandomValues(randomByte);
  const length = 6 + (randomByte[0] % 3);
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let secret = "";
  for (let i = 0; i < length; i++) {
    secret += chars[bytes[i] % chars.length];
  }
  return secret;
}

/* ---------- State ---------- */

interface ProvidersState {
  providers: ProviderSummary[];
  loading: boolean;
  selectedProvider: ProviderDetail | null;
  detailLoading: boolean;

  // Add form state
  showForm: boolean;
  formType: string;
  formDisplayName: string;
  formConfig: Record<string, string | boolean>;
  formErrors: FieldError[];
  formSubmitting: boolean;

  // Pairing secret display (shown after creation or reset)
  showPairingSecret: string | null;
  pairingSecretWsEndpoint: string | null;

  // Reset pairing state
  resetTarget: ProviderSummary | null;
  resetLoading: boolean;
  resetSecret: string | null;

  // Edit form state
  editingProvider: ProviderDetail | null;
  editDisplayName: string;
  editConfig: Record<string, string | boolean>;
  /** Snapshot of editConfig as loaded, used to detect changed fields on submit. */
  editConfigOriginal: Record<string, string | boolean>;
  editErrors: FieldError[];
  editSubmitting: boolean;

  // Delete confirmation
  deleteTarget: ProviderSummary | null;
  deleteLoading: boolean;

  // Modem status (for modem-gateway providers)
  modemStatus: ModemStatus | null;
  modemStatusLoading: boolean;

  // Sync state
  syncingProviderId: string | null;

  // Dummy provider testing (fake inbound SMS / call)
  dummySmsFrom: string;
  dummySmsBody: string;
  dummySmsSubmitting: boolean;
  dummyCallFrom: string;
  dummyCallSubmitting: boolean;
  // Provider call ID of the in-progress simulated inbound call, if any.
  dummyActiveCallId: string | null;

  // Notifications
  notifications: Notification[];
}

let notificationCounter = 0;

/* ---------- Component ---------- */

export class Providers extends Component<Record<string, never>, ProvidersState> {
  private statusPollTimer: ReturnType<typeof setInterval> | null = null;

  state: ProvidersState = {
    providers: [],
    loading: true,
    selectedProvider: null,
    detailLoading: false,
    showForm: false,
    formType: "vonage",
    formDisplayName: "",
    formConfig: buildDefaultConfig("vonage"),
    formErrors: [],
    formSubmitting: false,
    showPairingSecret: null,
    pairingSecretWsEndpoint: null,
    resetTarget: null,
    resetLoading: false,
    resetSecret: null,
    editingProvider: null,
    editDisplayName: "",
    editConfig: {},
    editConfigOriginal: {},
    editErrors: [],
    editSubmitting: false,
    deleteTarget: null,
    deleteLoading: false,
    modemStatus: null,
    modemStatusLoading: false,
    syncingProviderId: null,
    dummySmsFrom: "",
    dummySmsBody: "",
    dummySmsSubmitting: false,
    dummyCallFrom: "",
    dummyCallSubmitting: false,
    dummyActiveCallId: null,
    notifications: [],
  };

  componentDidMount() {
    this.fetchProviders();
  }

  componentWillUnmount() {
    this.stopStatusPolling();
  }

  /* ---------- Data fetching ---------- */

  private fetchProviders = async () => {
    this.setState({ loading: true });
    const result = await api.get<{ providers: ProviderSummary[] }>("/api/providers");
    if (result.ok) {
      this.setState({ providers: result.data.providers, loading: false });
    } else {
      this.setState({ loading: false });
      this.showNotification("Failed to load providers", "error");
    }
  };

  private fetchProviderDetail = async (id: string) => {
    this.setState({
      detailLoading: true,
      modemStatus: null,
      // Reset dummy testing inputs so they don't carry across providers.
      dummySmsFrom: "",
      dummySmsBody: "",
      dummyCallFrom: "",
      dummyActiveCallId: null,
    });
    const result = await api.get<ProviderDetail>(`/api/providers/${id}`);
    if (result.ok) {
      this.setState({ selectedProvider: result.data, detailLoading: false });
      // Start polling modem status for modem-gateway providers
      if (result.data.type === "modem-gateway") {
        this.fetchModemStatus(result.data.id);
        this.startStatusPolling(result.data.id);
      } else {
        this.stopStatusPolling();
      }
    } else {
      this.setState({ detailLoading: false });
      this.showNotification("Failed to load provider details", "error");
    }
  };

  private fetchModemStatus = async (id: string) => {
    this.setState({ modemStatusLoading: true });
    const result = await api.get<ModemStatus>(`/api/providers/${id}/status`);
    if (result.ok) {
      this.setState({ modemStatus: result.data, modemStatusLoading: false });
    } else {
      this.setState({ modemStatusLoading: false });
    }
  };

  private startStatusPolling(id: string) {
    this.stopStatusPolling();
    this.statusPollTimer = setInterval(() => {
      this.fetchModemStatus(id);
    }, 10_000);
  }

  private stopStatusPolling() {
    if (this.statusPollTimer) {
      clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }
  }

  /* ---------- Notifications ---------- */

  private showNotification = (message: string, type: "success" | "error") => {
    const id = ++notificationCounter;
    this.setState((prev) => ({
      notifications: [...prev.notifications, { message, type, id }],
    }));
    setTimeout(() => {
      this.setState((prev) => ({
        notifications: prev.notifications.filter((n) => n.id !== id),
      }));
    }, 4000);
  };

  /* ---------- Clipboard ---------- */

  private handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      this.showNotification(`${label} copied to clipboard`, "success");
    } catch {
      this.showNotification("Failed to copy to clipboard", "error");
    }
  };

  /* ---------- Toggle enable/disable ---------- */

  // Providers can be disabled via the REST API, but the web UI only offers
  // re-enabling one (disabling from the UI is intentionally not supported —
  // per-number controls cover that need).
  private handleEnable = async (provider: ProviderSummary) => {
    const result = await api.put<ProviderDetail>(`/api/providers/${provider.id}`, {
      enabled: true,
    });
    if (result.ok) {
      this.setState((prev) => ({
        providers: prev.providers.map((p) =>
          p.id === provider.id ? { ...p, enabled: true } : p
        ),
      }));
      this.showNotification(`Provider "${provider.displayName}" enabled`, "success");
    } else {
      const errorData = result.data as { error?: string };
      this.showNotification(errorData.error || "Failed to enable provider", "error");
    }
  };

  /* ---------- Delete flow ---------- */

  private handleDeleteClick = (provider: ProviderSummary) => {
    this.setState({ deleteTarget: provider });
  };

  private handleDeleteCancel = () => {
    this.setState({ deleteTarget: null });
  };

  private handleDeleteConfirm = async () => {
    const { deleteTarget } = this.state;
    if (!deleteTarget) return;

    this.setState({ deleteLoading: true });
    const result = await api.delete<void>(`/api/providers/${deleteTarget.id}`);

    if (result.ok || result.status === 204) {
      this.setState((prev) => ({
        providers: prev.providers.filter((p) => p.id !== deleteTarget.id),
        deleteTarget: null,
        deleteLoading: false,
        selectedProvider:
          prev.selectedProvider?.id === deleteTarget.id ? null : prev.selectedProvider,
      }));
      this.showNotification(`Provider "${deleteTarget.displayName}" removed`, "success");
    } else {
      const errorData = result.data as { error?: string };
      this.setState({ deleteTarget: null, deleteLoading: false });
      this.showNotification(errorData.error || "Failed to remove provider", "error");
    }
  };

  /* ---------- Sync handler ---------- */

  private handleSync = async (provider: ProviderSummary) => {
    this.setState({ syncingProviderId: provider.id });

    const result = await api.post<{ added: string[]; removed: string[]; total: number }>(
      "/api/numbers/sync",
      { providerId: provider.id }
    );

    this.setState({ syncingProviderId: null });

    if (result.ok) {
      const { added, removed, total } = result.data;
      if (added.length === 0 && removed.length === 0) {
        this.showNotification(`"${provider.displayName}" is up to date (${total} numbers)`, "success");
      } else {
        const parts: string[] = [];
        if (added.length > 0) parts.push(`${added.length} added`);
        if (removed.length > 0) parts.push(`${removed.length} removed`);
        this.showNotification(
          `Synced "${provider.displayName}": ${parts.join(", ")} (${total} total)`,
          "success"
        );
      }
    } else {
      const errorData = result.data as { error?: string };
      this.showNotification(errorData.error || "Sync failed", "error");
    }
  };

  /* ---------- Dummy provider testing ---------- */

  private handleSimulateSms = async (e: Event) => {
    e.preventDefault();
    const { selectedProvider, dummySmsFrom, dummySmsBody } = this.state;
    if (!selectedProvider) return;

    const from = dummySmsFrom.trim();
    const body = dummySmsBody.trim();
    if (!from || !body) {
      this.showNotification("Enter a from number and a message", "error");
      return;
    }

    this.setState({ dummySmsSubmitting: true });
    const result = await api.post(
      `/api/providers/${selectedProvider.id}/simulate-incoming-sms`,
      { from, body },
    );
    this.setState({ dummySmsSubmitting: false });

    if (result.ok) {
      this.setState({ dummySmsBody: "" });
      this.showNotification("Fake incoming SMS sent", "success");
    } else {
      const errorData = result.data as { error?: string };
      this.showNotification(errorData.error || "Failed to send fake SMS", "error");
    }
  };

  private handleSimulateCall = async (e: Event) => {
    e.preventDefault();
    const { selectedProvider, dummyCallFrom } = this.state;
    if (!selectedProvider) return;

    const from = dummyCallFrom.trim();
    if (!from) {
      this.showNotification("Enter a from number", "error");
      return;
    }

    this.setState({ dummyCallSubmitting: true });
    const result = await api.post<{ status: string; callId: string }>(
      `/api/providers/${selectedProvider.id}/simulate-incoming-call`,
      { from },
    );
    this.setState({ dummyCallSubmitting: false });

    if (result.ok) {
      this.setState({ dummyActiveCallId: result.data.callId });
      this.showNotification("Fake incoming call started", "success");
    } else {
      const errorData = result.data as { error?: string };
      this.showNotification(errorData.error || "Failed to start fake call", "error");
    }
  };

  private handleHangupCall = async (e: Event) => {
    e.preventDefault();
    const { selectedProvider, dummyActiveCallId } = this.state;
    if (!selectedProvider) return;

    this.setState({ dummyCallSubmitting: true });
    const result = await api.post(
      `/api/providers/${selectedProvider.id}/hangup-incoming-call`,
      dummyActiveCallId ? { callId: dummyActiveCallId } : {},
    );
    this.setState({ dummyCallSubmitting: false });

    if (result.ok) {
      this.setState({ dummyActiveCallId: null });
      this.showNotification("Fake caller hung up", "success");
    } else {
      const errorData = result.data as { error?: string };
      // If the call is already gone (e.g. answered+ended elsewhere), clear the
      // active state so the UI returns to the "start call" button.
      if (result.status === 404) {
        this.setState({ dummyActiveCallId: null });
      }
      this.showNotification(errorData.error || "Failed to hang up fake call", "error");
    }
  };

  /* ---------- Edit form handlers ---------- */

  private handleEditClick = (provider: ProviderDetail) => {
    // Convert config values to form values, replacing masked secrets with empty.
    const editConfig: Record<string, string | boolean> = {};
    const fields = CONFIG_FIELDS[provider.type] || [];
    for (const field of fields) {
      const value = provider.config[field.name];

      // UI-only toggles have no stored value; derive their initial state.
      if (field.name === "override_webhook_whitelist") {
        // Pre-check the override toggle when a custom allowlist is already saved
        // so the IP list field is revealed for editing.
        const stored = provider.config.webhook_ip_allowlist;
        const hasCustomList = Array.isArray(stored)
          ? stored.length > 0
          : typeof stored === "string" && stored.trim().length > 0;
        editConfig[field.name] = hasCustomList;
        continue;
      }

      if (field.type === "checkbox") {
        // Booleans are never masked; absent means "on by default".
        editConfig[field.name] =
          value === undefined ? (field.defaultChecked ?? false) : value !== false;
        continue;
      }
      // The IP allowlist may be stored as an array; present it as one entry per
      // line for the textarea.
      if (field.name === "webhook_ip_allowlist") {
        editConfig[field.name] = Array.isArray(value)
          ? value.join("\n")
          : typeof value === "string"
            ? value
            : "";
        continue;
      }
      // Masked values (e.g. "****abcd") should show as empty — user re-enters if changing
      const strValue = typeof value === "string" ? value : "";
      const isMasked = strValue.startsWith("*");
      editConfig[field.name] = isMasked ? "" : strValue;
    }

    this.setState({
      editingProvider: provider,
      editDisplayName: provider.displayName,
      editConfig,
      // Snapshot so submit can send only fields the user actually changed.
      editConfigOriginal: { ...editConfig },
      editErrors: [],
      selectedProvider: null, // close detail view
    });
  };

  private handleEditCancel = () => {
    this.setState({ editingProvider: null, editErrors: [] });
  };

  private handleEditDisplayNameChange = (e: Event) => {
    this.setState({ editDisplayName: (e.target as HTMLInputElement).value });
  };

  private handleEditConfigFieldChange = (field: string, value: string | boolean) => {
    this.setState((prev) => ({
      editConfig: { ...prev.editConfig, [field]: value },
    }));
  };

  private handleEditSubmit = async (e: Event) => {
    e.preventDefault();
    const { editingProvider, editDisplayName, editConfig, editConfigOriginal } = this.state;
    if (!editingProvider) return;

    this.setState({ editSubmitting: true, editErrors: [] });

    // Build the update payload. Only include fields the user actually changed;
    // omitted keys are left untouched by the server's config merge.
    const configUpdate: Record<string, string | boolean> = {};
    const fields = CONFIG_FIELDS[editingProvider.type] || [];
    const overrideOn = editConfig.override_webhook_whitelist === true;
    for (const field of fields) {
      // UI-only toggles are never sent to the backend.
      if (UI_ONLY_CONFIG_FIELDS.includes(field.name)) {
        continue;
      }

      // The IP allowlist is coupled to the override toggle. When the operator
      // turns the override off, clear any previously stored list so origin
      // checks fall back to the built-in 46elks defaults. When on, always send
      // the current textarea value (which may be empty = use defaults).
      if (field.name === "webhook_ip_allowlist") {
        const current = typeof editConfig[field.name] === "string" ? (editConfig[field.name] as string) : "";
        const original = typeof editConfigOriginal[field.name] === "string" ? (editConfigOriginal[field.name] as string) : "";
        if (!overrideOn) {
          // Only send an explicit clear when something was previously stored.
          if (original.trim().length > 0) {
            configUpdate[field.name] = "";
          }
        } else if (current !== original) {
          configUpdate[field.name] = current;
        }
        continue;
      }

      if (field.type === "checkbox") {
        // Booleans always have a real current value, so we can dirty-check
        // against the loaded snapshot and only send when it changed.
        const current = editConfig[field.name] === true;
        const original = editConfigOriginal[field.name] === true;
        if (current !== original) {
          configUpdate[field.name] = current;
        }
        continue;
      }
      // Text/secret fields: an empty field means "keep existing value" (secrets
      // come back masked, so we can't echo them). Only send non-empty values.
      const value = typeof editConfig[field.name] === "string" ? (editConfig[field.name] as string) : "";
      if (value.length > 0) {
        configUpdate[field.name] = value;
      }
    }

    const body: Record<string, unknown> = {};
    if (editDisplayName !== editingProvider.displayName) {
      body.displayName = editDisplayName;
    }
    if (Object.keys(configUpdate).length > 0) {
      body.config = configUpdate;
    }

    // If nothing changed, just close
    if (Object.keys(body).length === 0) {
      this.setState({ editingProvider: null, editSubmitting: false });
      return;
    }

    const result = await api.put<ProviderDetail>(
      `/api/providers/${editingProvider.id}`,
      body
    );

    if (result.ok) {
      // The update response reflects a fresh health check. If the saved config
      // is unhealthy (e.g. invalid credentials), keep the provider open and warn
      // instead of returning to the list — but the change IS saved either way.
      await this.fetchProviders();
      const updated = result.data;
      if (updated.status === "error") {
        this.stopStatusPolling();
        this.setState({
          editingProvider: null,
          editSubmitting: false,
          editErrors: [],
          selectedProvider: updated, // reopen detail so the error + reason are visible
          modemStatus: null,
        });
        this.showNotification(
          updated.healthReason
            ? `Saved, but the provider is in error: ${updated.healthReason}`
            : "Saved, but the provider is in an error state",
          "error",
        );
      } else {
        this.setState({
          editingProvider: null,
          editSubmitting: false,
          editErrors: [],
        });
        this.showNotification("Provider updated successfully", "success");
      }
    } else {
      const errorData = result.data as { error?: string; fieldErrors?: FieldError[] };
      this.setState({
        editSubmitting: false,
        editErrors: errorData.fieldErrors || [
          { field: "general", message: errorData.error || "Failed to update provider" },
        ],
      });
    }
  };

  /* ---------- Add form handlers ---------- */

  private handleShowForm = () => {
    this.setState({
      showForm: true,
      formType: "vonage",
      formDisplayName: "",
      formConfig: buildDefaultConfig("vonage"),
      formErrors: [],
    });
  };

  private handleCancelForm = () => {
    this.setState({ showForm: false, formErrors: [] });
  };

  private handleTypeChange = (e: Event) => {
    const value = (e.target as HTMLSelectElement).value;
    this.setState({ formType: value, formConfig: buildDefaultConfig(value), formErrors: [] });
  };

  private handleDisplayNameChange = (e: Event) => {
    const value = (e.target as HTMLInputElement).value;
    this.setState({ formDisplayName: value });
  };

  private handleConfigFieldChange = (field: string, value: string | boolean) => {
    this.setState((prev) => ({
      formConfig: { ...prev.formConfig, [field]: value },
    }));
  };

  private handleFormSubmit = async (e: Event) => {
    e.preventDefault();
    const { formType, formDisplayName, formConfig } = this.state;

    this.setState({ formSubmitting: true, formErrors: [] });

    // For modem-gateway, generate a pairing secret client-side
    const config = { ...formConfig } as Record<string, string | boolean>;
    // UI-only controls (e.g. the "override webhook whitelist" toggle) drive
    // field visibility but are not backend config; never persist them.
    for (const key of UI_ONLY_CONFIG_FIELDS) {
      delete config[key];
    }
    let generatedSecret: string | undefined;
    if (formType === "modem-gateway") {
      generatedSecret = generatePairingSecret();
      config.pairing_secret = generatedSecret;
    }

    const result = await api.post<{ providerId: string; webhookUrls: string[]; wsEndpoint?: string }>(
      "/api/providers",
      {
        type: formType,
        displayName: formDisplayName,
        config,
      }
    );

    if (result.ok) {
      // Refresh list to get new provider
      await this.fetchProviders();
      this.setState({
        showForm: false,
        formSubmitting: false,
        formDisplayName: "",
        formConfig: buildDefaultConfig(formType),
        formErrors: [],
      });

      // For modem-gateway, show the pairing secret prominently
      if (formType === "modem-gateway" && generatedSecret) {
        this.setState({
          showPairingSecret: generatedSecret,
          pairingSecretWsEndpoint: result.data.wsEndpoint || null,
        });
      } else {
        // Cloud providers: the create response reflects a fresh health check.
        // Open the new provider's detail so its status is visible, and warn if
        // it came back unhealthy (e.g. invalid credentials). It IS saved either
        // way. fetchProviderDetail loads the freshly-probed status/healthReason.
        const created = await api.get<ProviderDetail>(`/api/providers/${result.data.providerId}`);
        if (created.ok && created.data.status === "error") {
          this.setState({ selectedProvider: created.data });
          this.showNotification(
            created.data.healthReason
              ? `Saved, but the provider is in error: ${created.data.healthReason}`
              : "Saved, but the provider is in an error state",
            "error",
          );
        } else {
          this.showNotification("Provider added successfully", "success");
        }
      }
    } else {
      const errorData = result.data as { error?: string; fieldErrors?: FieldError[] };
      this.setState({
        formSubmitting: false,
        formErrors: errorData.fieldErrors || [
          { field: "general", message: errorData.error || "Failed to add provider" },
        ],
      });
    }
  };

  /* ---------- Detail view ---------- */

  private handleProviderClick = (provider: ProviderSummary) => {
    this.fetchProviderDetail(provider.id);
  };

  private handleCloseDetail = () => {
    this.stopStatusPolling();
    this.setState({ selectedProvider: null, modemStatus: null });
  };

  /* ---------- Pairing secret display ---------- */

  private handleDismissPairingSecret = () => {
    this.setState({ showPairingSecret: null, pairingSecretWsEndpoint: null });
    this.showNotification("Provider added successfully", "success");
  };

  /* ---------- Reset pairing ---------- */

  private handleResetPairingClick = (provider: ProviderSummary) => {
    this.setState({ resetTarget: provider, resetSecret: null });
  };

  private handleResetPairingCancel = () => {
    this.setState({ resetTarget: null, resetSecret: null });
  };

  private handleResetPairingConfirm = async () => {
    const { resetTarget } = this.state;
    if (!resetTarget) return;

    this.setState({ resetLoading: true });

    const newSecret = generatePairingSecret();
    const result = await api.post<{ wsEndpoint: string }>(
      `/api/providers/${resetTarget.id}/reset`,
      { pairingSecret: newSecret }
    );

    if (result.ok) {
      this.setState({
        resetTarget: null,
        resetLoading: false,
        showPairingSecret: newSecret,
        pairingSecretWsEndpoint: result.data.wsEndpoint || null,
      });
    } else {
      const errorData = result.data as { error?: string };
      this.setState({ resetTarget: null, resetLoading: false });
      this.showNotification(errorData.error || "Failed to reset pairing", "error");
    }
  };

  /* ---------- Render helpers ---------- */

  private renderNotifications() {
    const { notifications } = this.state;
    return (
      <Fragment>
        {notifications.map((n) => (
          <div
            key={n.id}
            class={`notification ${n.type}`}
            role="alert"
            aria-live="assertive"
          >
            {n.message}
          </div>
        ))}
      </Fragment>
    );
  }

  private renderDeleteDialog() {
    const { deleteTarget, deleteLoading } = this.state;
    if (!deleteTarget) return null;

    return (
      <div class="modal-overlay" role="dialog" aria-modal="true" aria-label="Confirm provider removal">
        <div class="modal-content card">
          <h3>Remove Provider</h3>
          <p>
            Are you sure you want to remove <strong>{deleteTarget.displayName}</strong> ({deleteTarget.type})?
            This action cannot be undone.
          </p>
          <div class="modal-actions">
            <button
              type="button"
              class="btn-secondary"
              onClick={this.handleDeleteCancel}
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn-danger"
              onClick={this.handleDeleteConfirm}
              disabled={deleteLoading}
              aria-busy={deleteLoading ? "true" : undefined}
            >
              {deleteLoading ? "Removing..." : "Remove"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render a labeled, boxed URL with a copy button on the right.
   * Used for webhook URLs and the 46elks audio WebSocket URL so they share
   * one consistent style.
   */
  private renderCopyableUrl(label: string, url: string, hint?: ComponentChildren) {
    return (
      <div key={url} class="ws-endpoint-display" aria-label={label}>
        <span class="ws-endpoint-label">{label}</span>
        <div class="ws-endpoint-row">
          <code class="ws-endpoint-value">{url}</code>
          <button
            type="button"
            class="btn-copy"
            onClick={() => this.handleCopy(url, label)}
            aria-label={`Copy ${label} to clipboard`}
            title="Copy to clipboard"
          >
            Copy
          </button>
        </div>
        {hint}
      </div>
    );
  }

  private renderPairingSecretDialog() {
    const { showPairingSecret, pairingSecretWsEndpoint } = this.state;
    if (!showPairingSecret) return null;

    return (
      <div class="modal-overlay" role="dialog" aria-modal="true" aria-label="Pairing secret">
        <div class="modal-content card">
          <h3>Modem Gateway Pairing Secret</h3>
          <p>
            Copy this secret into your modem-gateway configuration file under{" "}
            <code>connection.pairingSecret</code>. It will only be shown once.
          </p>
          <div class="pairing-secret-display" aria-label="Pairing secret value">
            <code class="pairing-secret-value">{showPairingSecret}</code>
          </div>
          {pairingSecretWsEndpoint && (
            <div class="ws-endpoint-display" aria-label="WebSocket connection URL">
              <span class="ws-endpoint-label">Connection URL</span>
              <code class="ws-endpoint-value">{pairingSecretWsEndpoint}</code>
              <p class="form-hint ws-endpoint-hint">
                Set this as <code>connection.url</code> in your modem-gateway configuration.
              </p>
            </div>
          )}
          <p class="form-hint">
            The secret expires after 24 hours. If you lose it, use "Reset Pairing" to generate a new one.
          </p>
          <div class="modal-actions">
            <button
              type="button"
              onClick={this.handleDismissPairingSecret}
            >
              I've copied the secret
            </button>
          </div>
        </div>
      </div>
    );
  }

  private renderResetPairingDialog() {
    const { resetTarget, resetLoading } = this.state;
    if (!resetTarget) return null;

    return (
      <div class="modal-overlay" role="dialog" aria-modal="true" aria-label="Confirm pairing reset">
        <div class="modal-content card">
          <h3>Reset Pairing</h3>
          <p>
            This will disconnect <strong>{resetTarget.displayName}</strong>, delete its stored key,
            and generate a new pairing secret. The modem-gateway binary will need to re-pair.
          </p>
          <div class="modal-actions">
            <button
              type="button"
              class="btn-secondary"
              onClick={this.handleResetPairingCancel}
              disabled={resetLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn-warning"
              onClick={this.handleResetPairingConfirm}
              disabled={resetLoading}
              aria-busy={resetLoading ? "true" : undefined}
            >
              {resetLoading ? "Resetting..." : "Reset Pairing"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  private renderAddForm() {
    const { showForm, formType, formDisplayName, formConfig, formErrors, formSubmitting } =
      this.state;

    if (!showForm) return null;

    const fields = CONFIG_FIELDS[formType] || [];

    const getFieldError = (fieldName: string): string | undefined => {
      const err = formErrors.find(
        (e) => e.field === fieldName || e.field === `config.${fieldName}`
      );
      return err?.message;
    };

    const generalError = formErrors.find((e) => e.field === "general");

    return (
      <form class="card add-provider-form" onSubmit={this.handleFormSubmit} noValidate>
        <h3>Add Provider</h3>

        {generalError && (
          <div class="form-error" role="alert">
            {generalError.message}
          </div>
        )}

        <div class="form-group">
          <label htmlFor="provider-type">Type</label>
          <select
            id="provider-type"
            value={formType}
            onChange={this.handleTypeChange}
            disabled={formSubmitting}
          >
            <option value="vonage">Vonage</option>
            <option value="46elks">46elks</option>
            <option value="modem-gateway">Modem Gateway</option>
            <option value="dummy">Dummy</option>
          </select>
          {getFieldError("type") && (
            <div class="form-error">{getFieldError("type")}</div>
          )}
        </div>

        <div class="form-group">
          <label htmlFor="provider-displayName">Display Name</label>
          <input
            id="provider-displayName"
            type="text"
            value={formDisplayName}
            onInput={this.handleDisplayNameChange}
            maxLength={100}
            placeholder="e.g. My Vonage Account"
            disabled={formSubmitting}
            aria-invalid={getFieldError("displayName") ? "true" : undefined}
          />
          {getFieldError("displayName") && (
            <div class="form-error">{getFieldError("displayName")}</div>
          )}
        </div>

        {fields.map((field) => {
          // Dependent fields (e.g. the IP override list) only render while the
          // checkbox they depend on is checked.
          if (field.showWhen && formConfig[field.showWhen] !== true) {
            return null;
          }
          if (field.type === "checkbox") {
            const checked = formConfig[field.name] === true;
            return (
              <div class="form-group" key={field.name}>
                <label class="checkbox-field" htmlFor={`provider-config-${field.name}`}>
                  <input
                    id={`provider-config-${field.name}`}
                    type="checkbox"
                    checked={checked}
                    onChange={(e: Event) =>
                      this.handleConfigFieldChange(field.name, (e.target as HTMLInputElement).checked)
                    }
                    disabled={formSubmitting}
                  />
                  <span class="toggle-label">{field.label}</span>
                </label>
                {field.hint && <p class="form-hint">{field.hint}</p>}
                {!checked && field.offHint && (
                  <p class="form-hint form-warning" role="alert">{field.offHint}</p>
                )}
              </div>
            );
          }
          return (
            <div class="form-group" key={field.name}>
              <label htmlFor={`provider-config-${field.name}`}>{field.label}</label>
              {field.type === "textarea" ? (
                <textarea
                  id={`provider-config-${field.name}`}
                  value={String(formConfig[field.name] ?? "")}
                  onInput={(e: Event) =>
                    this.handleConfigFieldChange(field.name, (e.target as HTMLTextAreaElement).value)
                  }
                  disabled={formSubmitting}
                  aria-invalid={getFieldError(field.name) ? "true" : undefined}
                  rows={6}
                  placeholder={field.placeholder ?? "-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"}
                />
              ) : (
                <input
                  id={`provider-config-${field.name}`}
                  type={field.type}
                  value={String(formConfig[field.name] ?? "")}
                  onInput={(e: Event) =>
                    this.handleConfigFieldChange(field.name, (e.target as HTMLInputElement).value)
                  }
                  disabled={formSubmitting}
                  aria-invalid={getFieldError(field.name) ? "true" : undefined}
                />
              )}
              {field.hint && <p class="form-hint">{field.hint}</p>}
              {getFieldError(field.name) && (
                <div class="form-error">{getFieldError(field.name)}</div>
              )}
            </div>
          );
        })}

        <div class="form-actions">
          <button
            type="button"
            class="btn-secondary"
            onClick={this.handleCancelForm}
            disabled={formSubmitting}
          >
            Cancel
          </button>
          <button type="submit" disabled={formSubmitting} aria-busy={formSubmitting ? "true" : undefined}>
            {formSubmitting ? "Adding..." : "Add Provider"}
          </button>
        </div>
      </form>
    );
  }

  private renderEditForm() {
    const { editingProvider, editDisplayName, editConfig, editErrors, editSubmitting } =
      this.state;

    if (!editingProvider) return null;

    const fields = CONFIG_FIELDS[editingProvider.type] || [];

    const getFieldError = (fieldName: string): string | undefined => {
      const err = editErrors.find(
        (e) => e.field === fieldName || e.field === `config.${fieldName}`
      );
      return err?.message;
    };

    const generalError = editErrors.find((e) => e.field === "general");

    return (
      <form class="card edit-provider-form" onSubmit={this.handleEditSubmit} noValidate>
        <h3>Edit Provider: {editingProvider.displayName}</h3>
        <p class="form-hint">
          Leave secret fields empty to keep their current values.
        </p>

        {generalError && (
          <div class="form-error" role="alert">
            {generalError.message}
          </div>
        )}

        <div class="form-group">
          <label htmlFor="edit-provider-displayName">Display Name</label>
          <input
            id="edit-provider-displayName"
            type="text"
            value={editDisplayName}
            onInput={this.handleEditDisplayNameChange}
            maxLength={100}
            disabled={editSubmitting}
            aria-invalid={getFieldError("displayName") ? "true" : undefined}
          />
          {getFieldError("displayName") && (
            <div class="form-error">{getFieldError("displayName")}</div>
          )}
        </div>

        {fields.map((field) => {
          // Dependent fields (e.g. the IP override list) only render while the
          // checkbox they depend on is checked.
          if (field.showWhen && editConfig[field.showWhen] !== true) {
            return null;
          }
          if (field.type === "checkbox") {
            const checked = editConfig[field.name] === true;
            return (
              <div class="form-group" key={field.name}>
                <label class="checkbox-field" htmlFor={`edit-provider-config-${field.name}`}>
                  <input
                    id={`edit-provider-config-${field.name}`}
                    type="checkbox"
                    checked={checked}
                    onChange={(e: Event) =>
                      this.handleEditConfigFieldChange(field.name, (e.target as HTMLInputElement).checked)
                    }
                    disabled={editSubmitting}
                  />
                  <span class="toggle-label">{field.label}</span>
                </label>
                {/* Edit view: keep only a short muted hint. The prominent
                    off-state warning lives in the provider detail view. */}
                {field.hint && <p class="form-hint">{field.hint}</p>}
                {!checked && field.offHint && (
                  <p class="form-hint">{field.offHint}</p>
                )}
              </div>
            );
          }
          return (
            <div class="form-group" key={field.name}>
              <label htmlFor={`edit-provider-config-${field.name}`}>{field.label}</label>
              {field.type === "textarea" ? (
                <textarea
                  id={`edit-provider-config-${field.name}`}
                  value={String(editConfig[field.name] ?? "")}
                  onInput={(e: Event) =>
                    this.handleEditConfigFieldChange(field.name, (e.target as HTMLTextAreaElement).value)
                  }
                  disabled={editSubmitting}
                  aria-invalid={getFieldError(field.name) ? "true" : undefined}
                  rows={6}
                  placeholder={field.placeholder ?? "Leave empty to keep current value"}
                />
              ) : (
                <input
                  id={`edit-provider-config-${field.name}`}
                  type={field.type}
                  value={String(editConfig[field.name] ?? "")}
                  onInput={(e: Event) =>
                    this.handleEditConfigFieldChange(field.name, (e.target as HTMLInputElement).value)
                  }
                  disabled={editSubmitting}
                  aria-invalid={getFieldError(field.name) ? "true" : undefined}
                  placeholder={field.type === "password" ? "Leave empty to keep current value" : (field.placeholder ?? "")}
                />
              )}
              {field.hint && <p class="form-hint">{field.hint}</p>}
              {getFieldError(field.name) && (
                <div class="form-error">{getFieldError(field.name)}</div>
              )}
            </div>
          );
        })}

        <div class="form-actions">
          <button
            type="button"
            class="btn-secondary"
            onClick={this.handleEditCancel}
            disabled={editSubmitting}
          >
            Cancel
          </button>
          <button type="submit" disabled={editSubmitting} aria-busy={editSubmitting ? "true" : undefined}>
            {editSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    );
  }

  private renderModemStatus() {
    const { modemStatus, modemStatusLoading } = this.state;

    if (modemStatusLoading && !modemStatus) {
      return (
        <div class="modem-status-section">
          <h4>Gateway Connection</h4>
          <p class="form-hint">Loading status...</p>
        </div>
      );
    }

    if (!modemStatus) return null;

    // `signal` is a percentage (0-100) as reported by the gateway. A value of 0
    // means no/unknown signal. Bars map each 20% to one of five bars.
    const signalBars = modemStatus.signal != null ? Math.min(Math.max(Math.round(modemStatus.signal / 20), 0), 5) : 0;
    const signalLabel = modemStatus.signal != null ? `${modemStatus.signal}%` : "Unknown";

    return (
      <Fragment>
        {/* Section 1: Connection to Svarla server */}
        <div class="modem-status-section">
          <h4>Gateway Connection</h4>
          <p class="form-hint">Connection between the modem gateway and the Svarla server.</p>
          <div class="modem-connection-indicator">
            <span class={`connection-dot ${modemStatus.connected ? "connected" : "disconnected"}`}
                  aria-hidden="true" />
            <span class={modemStatus.connected ? "text-connected" : "text-disconnected"}>
              {modemStatus.connected ? "Connected" : "Disconnected"}
            </span>
          </div>
        </div>

        {/* Section 2: Modem / provider health */}
        {modemStatus.connected && (
          <div class="modem-status-section">
            <h4>Modem Status</h4>
            <p class="form-hint">Status reported by the modem hardware.</p>

            {modemStatus.modemUnsupportedWarning && (
              <div class="modem-warning" role="alert">
                {modemStatus.modemUnsupportedWarning}
              </div>
            )}

            <div class="modem-signal">
              <span class="signal-bars" aria-label={`Signal strength: ${signalLabel}`}>
                {[1, 2, 3, 4, 5].map((bar) => (
                  <span
                    key={bar}
                    class={`signal-bar ${bar <= signalBars ? "active" : ""}`}
                  />
                ))}
              </span>
              <span class="signal-label">{signalLabel}</span>
            </div>

            <dl class="detail-list modem-detail-list">
              {modemStatus.network && (
                <Fragment>
                  <dt>Network</dt>
                  <dd>{modemStatus.network}</dd>
                </Fragment>
              )}
              {modemStatus.networkTech && (
                <Fragment>
                  <dt>Technology</dt>
                  <dd>{modemStatus.networkTech}</dd>
                </Fragment>
              )}
              {modemStatus.band && (
                <Fragment>
                  <dt>Band</dt>
                  <dd>{modemStatus.band}</dd>
                </Fragment>
              )}
              {modemStatus.operator && (
                <Fragment>
                  <dt>Operator</dt>
                  <dd>{modemStatus.operator}</dd>
                </Fragment>
              )}
              {modemStatus.modemModel && (
                <Fragment>
                  <dt>Modem</dt>
                  <dd>
                    {modemStatus.modemManufacturer && `${modemStatus.modemManufacturer} `}
                    {modemStatus.modemModel}
                  </dd>
                </Fragment>
              )}
              {modemStatus.firmware && (
                <Fragment>
                  <dt>Firmware</dt>
                  <dd class="monospace">{modemStatus.firmware}</dd>
                </Fragment>
              )}
              {modemStatus.imei && (
                <Fragment>
                  <dt>IMEI</dt>
                  <dd class="monospace">{modemStatus.imei}</dd>
                </Fragment>
              )}
              {modemStatus.imsi && (
                <Fragment>
                  <dt>IMSI</dt>
                  <dd class="monospace">{modemStatus.imsi}</dd>
                </Fragment>
              )}
              {modemStatus.iccid && (
                <Fragment>
                  <dt>ICCID</dt>
                  <dd class="monospace">{modemStatus.iccid}</dd>
                </Fragment>
              )}
              {modemStatus.msisdn && (
                <Fragment>
                  <dt>Phone number</dt>
                  <dd class="monospace">{modemStatus.msisdn}</dd>
                </Fragment>
              )}
            </dl>

            {modemStatus.stale && modemStatus.stale.length > 0 && (
              <p class="form-hint modem-stale-warning">
                Stale data: {modemStatus.stale.join(", ")}
              </p>
            )}
          </div>
        )}
      </Fragment>
    );
  }

  /**
   * Dummy-provider testing controls: send a fake inbound SMS and start a fake
   * inbound call, both targeting the dummy provider's own number. Useful for
   * exercising client messaging and inbound-call signaling without a carrier.
   */
  private renderDummyTesting() {
    const {
      dummySmsFrom,
      dummySmsBody,
      dummySmsSubmitting,
      dummyCallFrom,
      dummyCallSubmitting,
      dummyActiveCallId,
    } = this.state;

    const callActive = dummyActiveCallId !== null;

    return (
      <div class="detail-section dummy-testing">
        <h4>Testing</h4>

        <form class="dummy-test-form" onSubmit={this.handleSimulateSms}>
          <h5>Fake incoming SMS</h5>
          <div class="form-field">
            <label for="dummy-sms-from">From number</label>
            <input
              id="dummy-sms-from"
              type="text"
              value={dummySmsFrom}
              placeholder="+15551234567"
              onInput={(e: Event) =>
                this.setState({ dummySmsFrom: (e.target as HTMLInputElement).value })
              }
            />
          </div>
          <div class="form-field">
            <label for="dummy-sms-body">Message</label>
            <textarea
              id="dummy-sms-body"
              value={dummySmsBody}
              placeholder="Message text"
              rows={2}
              onInput={(e: Event) =>
                this.setState({ dummySmsBody: (e.target as HTMLTextAreaElement).value })
              }
            />
          </div>
          <button type="submit" class="btn-sm" disabled={dummySmsSubmitting}>
            {dummySmsSubmitting ? "Sending..." : "Send fake SMS"}
          </button>
        </form>

        <form
          class="dummy-test-form"
          onSubmit={callActive ? this.handleHangupCall : this.handleSimulateCall}
        >
          <h5>Fake incoming call</h5>
          <div class="form-field">
            <label for="dummy-call-from">From number</label>
            <input
              id="dummy-call-from"
              type="text"
              value={dummyCallFrom}
              placeholder="+15551234567"
              disabled={callActive}
              onInput={(e: Event) =>
                this.setState({ dummyCallFrom: (e.target as HTMLInputElement).value })
              }
            />
          </div>
          {callActive ? (
            <button type="submit" class="btn-sm btn-warning" disabled={dummyCallSubmitting}>
              {dummyCallSubmitting ? "Hanging up..." : "Hang up (caller)"}
            </button>
          ) : (
            <button type="submit" class="btn-sm" disabled={dummyCallSubmitting}>
              {dummyCallSubmitting ? "Calling..." : "Simulate incoming call"}
            </button>
          )}
        </form>
      </div>
    );
  }

  private renderProviderDetail() {
    const { selectedProvider, detailLoading } = this.state;

    if (detailLoading) {
      return (
        <div class="card provider-detail">
          <p>Loading provider details...</p>
        </div>
      );
    }

    if (!selectedProvider) return null;

    return (
      <div class="card provider-detail">
        <div class="detail-header">
          <h3>{selectedProvider.displayName}</h3>
          <div class="detail-header-actions">
            <button
              type="button"
              class="btn-icon btn-icon-danger"
              onClick={() => this.handleDeleteClick(selectedProvider)}
              aria-label={`Remove ${selectedProvider.displayName}`}
              title={`Remove ${selectedProvider.displayName}`}
            >
              {trashIcon()}
            </button>
            <button
              type="button"
              class="btn-sm"
              onClick={() => this.handleEditClick(selectedProvider)}
              aria-label={`Edit ${selectedProvider.displayName}`}
            >
              Edit
            </button>
            <button type="button" class="btn-secondary btn-sm" onClick={this.handleCloseDetail}>
              Close
            </button>
          </div>
        </div>
        <dl class="detail-list">
          <dt>Type</dt>
          <dd>{selectedProvider.type}</dd>
          <dt>Status</dt>
          <dd>
            <span class={`badge ${
              selectedProvider.status === "ok" ? "badge-ok" :
              selectedProvider.status === "not_connected" ? "badge-not-connected" :
              selectedProvider.status === "disabled" ? "badge-disabled" :
              "badge-error"
            }`}>
              {providerStatusLabel(selectedProvider)}
            </span>
            {selectedProvider.status === "error" && selectedProvider.healthReason && (
              <p class="form-hint status-error-reason">{selectedProvider.healthReason}</p>
            )}
          </dd>
          <dt>ID</dt>
          <dd class="monospace">{selectedProvider.id}</dd>
        </dl>

        {selectedProvider.type === "vonage" &&
          selectedProvider.config.webhook_validation === false && (
            <p class="form-hint form-warning" role="alert">
              Webhook validation is off. Incoming webhooks are not signature-verified,
              so anyone who knows the webhook URL can post events to this provider.
            </p>
          )}

        {selectedProvider.type === "46elks" &&
          selectedProvider.config.webhook_validation === false && (
            <p class="form-hint form-warning" role="alert">
              Webhook validation is off. Incoming webhooks are not origin-verified,
              so anyone who knows the webhook URL can post events to this provider.
            </p>
          )}

        {selectedProvider.type === "modem-gateway" && this.renderModemStatus()}

        {Object.keys(selectedProvider.config).length > 0 && (
          <Fragment>
            <h4>Configuration</h4>
            <dl class="detail-list">
              {Object.entries(selectedProvider.config).map(([key, value]) => (
                <Fragment key={key}>
                  <dt>{configKeyLabel(key)}</dt>
                  {/* Booleans render as plain On/Off text; raw string values
                      (IDs, masked secrets, URLs) stay monospace. */}
                  <dd class={typeof value === "boolean" ? undefined : "monospace"}>
                    {configValueDisplay(value)}
                  </dd>
                </Fragment>
              ))}
            </dl>
          </Fragment>
        )}

        {selectedProvider.webhookUrls.length > 0 && (
          <Fragment>
            <h4>Webhook URLs</h4>
            {selectedProvider.type === "46elks" && (
              <p class="form-hint">
                Configure these in the 46elks dashboard for your number.
              </p>
            )}
            {selectedProvider.webhookUrls.map((url) =>
              this.renderCopyableUrl(webhookEndpointLabel(url), url),
            )}
          </Fragment>
        )}

        {selectedProvider.type === "46elks" && selectedProvider.audioWsUrl && (
          <Fragment>
            <h4>Audio WebSocket URL</h4>
            {this.renderCopyableUrl(
              "Register in 46elks dashboard",
              selectedProvider.audioWsUrl,
              <p class="form-hint ws-endpoint-hint">
                Set this as the WebSocket URL for your 46elks WebSocket number
                (the <code>+4600…</code> number) so call audio reaches the MediaBridge.
              </p>,
            )}
          </Fragment>
        )}

        {selectedProvider.type === "modem-gateway" && (
          <div class="detail-section">
            <button
              type="button"
              class="btn-warning btn-sm"
              onClick={() => this.handleResetPairingClick(selectedProvider)}
              aria-label={`Reset pairing for ${selectedProvider.displayName}`}
            >
              Reset Pairing
            </button>
          </div>
        )}

        {selectedProvider.type === "dummy" && this.renderDummyTesting()}
      </div>
    );
  }

  private renderProviderRow(provider: ProviderSummary) {
    return (
      <li
        key={provider.id}
        class="card provider-card"
        onClick={() => this.handleProviderClick(provider)}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.handleProviderClick(provider);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`View details for ${provider.displayName}`}
      >
        <div class="provider-info">
          <span class="provider-name">{provider.displayName}</span>
          {/* Only surface a status badge when there's something to flag —
              an OK provider shows nothing, keeping the list quiet. */}
          {provider.status !== "ok" && (
            <span class={`provider-status badge ${
              provider.status === "not_connected" ? "badge-not-connected" :
              provider.status === "disabled" ? "badge-disabled" :
              "badge-error"
            }`}>
              {providerStatusLabel(provider)}
            </span>
          )}
        </div>
        <div class="provider-actions">
          {provider.type !== "modem-gateway" && (
            <button
              type="button"
              class="btn-sm btn-secondary"
              onClick={(e: MouseEvent) => { e.stopPropagation(); this.handleSync(provider); }}
              disabled={this.state.syncingProviderId === provider.id || !provider.enabled}
              aria-label={`Sync numbers for ${provider.displayName}`}
              aria-busy={this.state.syncingProviderId === provider.id ? "true" : undefined}
            >
              {this.state.syncingProviderId === provider.id ? "Syncing..." : "Sync"}
            </button>
          )}
          {/* Providers can only be disabled via the REST API; the web UI
              offers re-enabling if that has happened. */}
          {!provider.enabled && (
            <button
              type="button"
              class="btn-sm btn-success-outline"
              onClick={(e: MouseEvent) => { e.stopPropagation(); this.handleEnable(provider); }}
              aria-label={`Enable ${provider.displayName}`}
            >
              Enable
            </button>
          )}
        </div>
      </li>
    );
  }

  private renderProviderList() {
    const { providers, loading, selectedProvider, editingProvider } = this.state;

    if (loading) {
      return <p>Loading providers...</p>;
    }

    if (providers.length === 0) {
      return <p class="empty-state">No providers configured. Add one to get started.</p>;
    }

    // Hide the row for the provider currently open in the detail or edit view,
    // so it doesn't appear duplicated below the panel that "expanded" from it.
    const openProviderId = editingProvider?.id ?? selectedProvider?.id ?? null;
    const visibleProviders = openProviderId
      ? providers.filter((p) => p.id !== openProviderId)
      : providers;

    if (visibleProviders.length === 0) {
      return null;
    }

    // Group providers by type; the type becomes a section header, so it no
    // longer needs to appear on each row. Groups preserve first-seen order.
    const groups: { type: string; providers: ProviderSummary[] }[] = [];
    for (const provider of visibleProviders) {
      let group = groups.find((g) => g.type === provider.type);
      if (!group) {
        group = { type: provider.type, providers: [] };
        groups.push(group);
      }
      group.providers.push(provider);
    }

    return (
      <div class="provider-groups">
        {groups.map((group) => (
          <section
            key={group.type}
            class="provider-type-group"
            aria-labelledby={`provider-type-${group.type}`}
          >
            <h3 id={`provider-type-${group.type}`} class="provider-group-title">
              {providerTypeLabel(group.type)}
            </h3>
            <ul class="provider-list" role="list">
              {group.providers.map((provider) => this.renderProviderRow(provider))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  render() {
    const { showForm } = this.state;

    return (
      <div class="providers-page">
        <div class="page-header">
          <h2>Providers</h2>
          {!showForm && (
            <button type="button" onClick={this.handleShowForm}>
              Add Provider
            </button>
          )}
        </div>

        {this.renderNotifications()}
        {this.renderPairingSecretDialog()}
        {this.renderResetPairingDialog()}
        {this.renderAddForm()}
        {this.renderEditForm()}
        {this.renderProviderDetail()}
        {this.renderDeleteDialog()}
        {this.renderProviderList()}
      </div>
    );
  }
}
